import { provideAppInitializer } from '@angular/core';

/** localStorage key holding the user's theme preference. */
export const THEME_STORAGE_KEY = 'theme';

/**
 * The user's theme *preference*.
 * - `'system'` — follow the OS (`prefers-color-scheme`), and keep following it live.
 * - `'dark'` / `'light'` — force that theme regardless of the OS.
 */
export type ThemePreference = 'system' | 'dark' | 'light';

/** Cycle order for the top-bar toggle: System → Dark → Light → System. */
export const THEME_ORDER: readonly ThemePreference[] = ['system', 'dark', 'light'];

/**
 * The OS dark-mode media query, or `null` when `matchMedia` is unavailable (SSR / older
 * environments / jsdom without a polyfill). Centralised so every reader is guarded.
 */
function darkMediaQuery(): MediaQueryList | null {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  } catch {
    return null;
  }
}

/**
 * Read the persisted preference. Defaults to `'system'` (follow the OS), and tolerates the
 * legacy 2-state values (`'light'` / `'dark'`) written by the old toggle.
 */
export function getStoredThemePreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'system' || raw === 'dark' || raw === 'light') return raw;
  } catch {
    /* SSR / storage unavailable */
  }
  return 'system';
}

/** Persist the preference. Swallows storage errors (private mode / SSR). */
export function storeThemePreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve a preference to the concrete theme to render. `'system'` reads the OS via
 * `matchMedia`; when that's unavailable it falls back to `'light'`.
 */
export function resolveEffectiveTheme(pref: ThemePreference): 'dark' | 'light' {
  if (pref === 'dark' || pref === 'light') return pref;
  return darkMediaQuery()?.matches ? 'dark' : 'light';
}

/**
 * Apply a preference by writing the *resolved* theme onto `<html data-theme>`. We always set an
 * explicit `dark`/`light` (never leave the attribute absent) so every existing
 * `[data-theme="dark"]` rule in `theme.css` applies for System-dark exactly as for forced Dark —
 * no CSS duplication needed (chosen Option B; see docs/ui-redesign/PLAN.md@ee136e6f in git history).
 */
export function applyThemePreference(pref: ThemePreference): void {
  try {
    document.documentElement.setAttribute('data-theme', resolveEffectiveTheme(pref));
  } catch {
    /* SSR */
  }
}

/** Back-compat helper: apply whatever preference is currently persisted. */
export function applyStoredTheme(): void {
  applyThemePreference(getStoredThemePreference());
}

/**
 * While the persisted preference is `'system'`, re-apply the theme whenever the OS light/dark
 * setting changes, so the app follows the OS live. Returns a teardown function. No-op when
 * `matchMedia` is unavailable.
 */
export function watchSystemTheme(): () => void {
  const mq = darkMediaQuery();
  if (!mq) return () => undefined;
  const onChange = (): void => {
    if (getStoredThemePreference() === 'system') applyStoredTheme();
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/**
 * Restore the persisted theme during app bootstrap (so a reload keeps the user's choice) and
 * follow the OS while in System mode. Add to each app's `appConfig` providers. The runtime
 * toggle lives in `lib-top-bar` (`TopBar.cycleTheme`).
 */
export function provideTheme() {
  return provideAppInitializer(() => {
    applyStoredTheme();
    watchSystemTheme();
  });
}
